import { Link } from "react-router-dom";
import { ChatCircleText, Phone, WhatsappLogo } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth-context";
import { PHONE_NUMBER, PHONE_TEL, SMS_HREF, WHATSAPP_HREF } from "@/components/ContactChannels";

/**
 * Public-pages header. Used on landing, pricing, about, contact.
 * Includes the Auditor logo, sister-group tagline, phone CTA, and navigation.
 */
export default function PublicHeader({ active }) {
  const { user } = useAuth();
  const link = (label, to, key) => {
    const cls = active === key
      ? "text-ink font-semibold border-b-2 border-brand"
      : "text-ink-muted hover:text-ink border-b-2 border-transparent";
    return (
      <Link
        to={to}
        data-testid={`public-nav-${key}`}
        className={`px-1 py-1 text-sm transition-colors ${cls}`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="border-b border-border bg-white sticky top-0 z-30">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        <div className="inline-flex items-center gap-3">
          <Link to="/" className="inline-flex items-center" data-testid="public-logo">
            <Logo variant="light" size="h-10" />
          </Link>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[10px] mono tracking-wider text-ink-muted">DIVISION OF</span>
            <a
              href="https://www.yellowthinking.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold text-ink hover:text-brand transition-colors"
              data-testid="header-yt-link"
            >
              Yellow Thinking
            </a>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-6">
          {link("Home", "/", "home")}
          {link("Pricing", "/pricing", "pricing")}
          {link("About", "/about", "about")}
          {link("Contact", "/contact", "contact")}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden lg:flex items-center gap-1 mr-1">
            <a
              href={PHONE_TEL}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-ink hover:text-brand transition-colors"
              data-testid="header-phone-link"
              title={`Call ${PHONE_NUMBER}`}
            >
              <Phone size={14} weight="fill" /> {PHONE_NUMBER}
            </a>
            <a
              href={SMS_HREF}
              className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-border text-ink-muted hover:border-ink hover:text-ink transition-colors"
              data-testid="header-sms-link"
              title="Send an SMS"
            >
              <ChatCircleText size={14} weight="fill" />
            </a>
            <a
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-border text-ink-muted hover:border-ink hover:text-ink transition-colors"
              data-testid="header-whatsapp-link"
              title="Chat on WhatsApp"
            >
              <WhatsappLogo size={14} weight="fill" />
            </a>
          </div>
          {user ? (
            <Link to="/app/dashboard" data-testid="header-app-cta"
              className="bg-ink text-white px-4 py-2 text-sm font-semibold rounded-sm hover:bg-brand transition-colors">
              Open App
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link"
                className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-ink hover:text-brand transition-colors">
                Sign in
              </Link>
              <Link to="/register" data-testid="header-register-link"
                className="bg-ink text-white px-4 py-2 text-sm font-semibold rounded-sm hover:bg-brand transition-colors">
                Start free
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
